import * as THREE from 'three';
import mapboxgl from 'mapbox-gl';
import distance from '@turf/distance';
import { CSS2DRenderer, CSS2DObject } from './CSS2DRenderer';

const TOOLTIP_ADDITIONAL_ALTITUDE_IN_METERS = 5;
const MODEL_ALTITUDE = 0;
const MODEL_ROTATION = [Math.PI / 2, 0, 0];

const TOOLTIPS_SCALE_ZOOM = {
  minZoom: 14,
  maxZoom: 16,
};

const ACTIVE_TOOLTIP_CSS_CLASS = {
  expanded: 'expanded',
  hovered: 'hovered',
};

const CSS_VARIABLE = {
  tooltipsCount: '--tooltips-count',
  tooltipScale: '--tooltip-scale',
};

const getTooltipScenePosition = (origin, centroid, altitude) => {
  /**
   * ℹ️ Пояснение
   * На сцене ThreeJS оси X и Z расположены параллельно земле,
   * а ось Y — перпендикулярно.
   * Положение объекта определяется относительно начала системы координат.
   * Точка (0, 0, 0) — центр ЖК, относительно него и делаются расчеты.
   * Координаты на сцене задаются в метрах.
   */
  const [lon1, lat1] = origin;
  const [lon2, lat2] = centroid;

  let x;
  let z;
  const y = altitude + TOOLTIP_ADDITIONAL_ALTITUDE_IN_METERS;

  x = distance([lon1, lat1], [lon2, lat1], { units: 'meters' });
  z = distance([lon1, lat1], [lon1, lat2], { units: 'meters' });

  x = lon1 > lon2 ? x * -1 : x;
  z = lat1 > lat2 ? z : z * -1;

  return new THREE.Vector3(x, y, z);
};

const createTooltip = (tooltip) => {
  const root = document.createElement('div');

  root.innerHTML = `
    <div class="tooltip-content mapboxgl-popup-anchor-bottom">
      <div class="mapboxgl-popup-tip"></div>

      <div class="mapboxgl-popup-content">
        <div class="tooltip-details-short">
          <span>${tooltip.flatsCountAvailableForSale}</span>
        </div>

        <div class="tooltip-details-full">
          <span class="house-name">${tooltip.houseName}</span>
          <span class="flats-count">
            ${tooltip.flatsCountAvailableForSale} ${
    window.currentLanguage === 'ru' ? 'в продаже' : 'у продажу'
  }
          </span>
          <span class="commissioning-date">${tooltip.commissioningDate}</span>
        </div>
      </div>
    </div>
  `;

  /**
   * Блокируем всплывание ивента к карте mapbox,
   * чтобы не тригерились события карты, пока курсор находится в попапе
   */
  root.addEventListener('mousemove', (event) => {
    event.stopPropagation();
  });

  root.dataset.houseId = tooltip.houseId;

  root.classList.add('house-tooltip');

  if (tooltip.expanded) {
    root.classList.add(ACTIVE_TOOLTIP_CSS_CLASS.expanded);
  }

  return root;
};

const isChromeIos = navigator.userAgent.indexOf('CriOS') >= 0;

export class House3DTooltipsLayer {
  id = 'house-3d-tooltips-layer';

  type = 'custom';

  renderingMode = '3d';

  selectedHouseIds = [];

  hoveredHouseId = null;

  constructor(config) {
    this.config = config;

    const modelAsMercatorCoordinate = mapboxgl.MercatorCoordinate.fromLngLat(
      config.origin,
      MODEL_ALTITUDE,
    );

    this.modelTransform = {
      translateX: modelAsMercatorCoordinate.x,
      translateY: modelAsMercatorCoordinate.y,
      translateZ: modelAsMercatorCoordinate.z,
      rotateX: MODEL_ROTATION[0],
      rotateY: MODEL_ROTATION[1],
      rotateZ: MODEL_ROTATION[2],
      scale: modelAsMercatorCoordinate.meterInMercatorCoordinateUnits(),
    };

    this.selectedHouseIds = config.selectedHouseIds ?? [];
  }

  onAdd = (map, gl) => {
    this.map = map;

    this.camera = new THREE.Camera();
    this.scene = new THREE.Scene();

    this.initRenderers(map, gl);

    this.addTooltipObjectsToScene(this.scene);

    this.updateTooltipsScale();
    this.updateTooltipsRendererSize();

    window.addEventListener('resize', this.updateTooltipsRendererSize);
    map.on('zoom', this.updateTooltipsScale);

    if (!window.isMobile) {
      map.on('mousemove', this.config.sectionsLayerId, this.handleMouseMoveSection);
      map.on('mouseleave', this.config.sectionsLayerId, this.handleMouseLeaveSection);
    }
  };

  onRemove = () => {
    window.removeEventListener('resize', this.updateTooltipsRendererSize);
    this.map.off('zoom', this.updateTooltipsScale);

    if (!window.isMobile) {
      this.map.off('mousemove', this.config.sectionsLayerId, this.handleMouseMoveSection);
      this.map.off('mouseleave', this.config.sectionsLayerId, this.handleMouseLeaveSection);
    }
  };

  render = (gl, matrix) => {
    const rotationX = new THREE.Matrix4().makeRotationAxis(
      new THREE.Vector3(1, 0, 0),
      this.modelTransform.rotateX,
    );
    const rotationY = new THREE.Matrix4().makeRotationAxis(
      new THREE.Vector3(0, 1, 0),
      this.modelTransform.rotateY,
    );
    const rotationZ = new THREE.Matrix4().makeRotationAxis(
      new THREE.Vector3(0, 0, 1),
      this.modelTransform.rotateZ,
    );

    const m = new THREE.Matrix4().fromArray(matrix);
    const l = new THREE.Matrix4()
      .makeTranslation(
        this.modelTransform.translateX,
        this.modelTransform.translateY,
        this.modelTransform.translateZ,
      )
      .scale(
        new THREE.Vector3(
          this.modelTransform.scale,
          -this.modelTransform.scale,
          this.modelTransform.scale,
        ),
      )
      .multiply(rotationX)
      .multiply(rotationY)
      .multiply(rotationZ);

    this.camera.projectionMatrix = m.multiply(l);

    this.renderer.resetState();
    this.renderer.render(this.scene, this.camera);

    this.tooltipsRenderer.render(this.scene, this.camera);

    this.map.triggerRepaint();
  };

  initRenderers = (map, gl) => {
    this.renderer = new THREE.WebGLRenderer({
      canvas: map.getCanvas(),
      context: gl,
      antialias: true,
    });

    this.renderer.autoClear = false;

    const tooltipsCount = this.config.tooltips.length;

    this.tooltipsRenderer = new CSS2DRenderer();
    this.tooltipsRenderer.domElement.style.position = 'absolute';
    this.tooltipsRenderer.domElement.style.setProperty(CSS_VARIABLE.tooltipsCount, tooltipsCount);

    this.tooltipsRenderer.domElement.style.top = 0;
    this.tooltipsRenderer.domElement.className = 'tooltips-renderer';
    this.map.getCanvasContainer().appendChild(this.tooltipsRenderer.domElement);

    /**
     * В Chrome на iOS дизейблим анимации из-за бага, связаного с торможением анимаций в этом браузере
     * Смотрите:
     * - https://bugs.chromium.org/p/chromium/issues/detail?id=899130
     * - https://bugs.chromium.org/p/chromium/issues/detail?id=1249723
     * - https://bugs.chromium.org/p/chromium/issues/detail?id=1231712
     */
    if (isChromeIos) {
      this.tooltipsRenderer.domElement.classList.add('-no-transitions');
    }
  };

  addTooltipObjectsToScene = (scene) => {
    this.config.tooltips.forEach((tooltip) => {
      const tooltipPosition = getTooltipScenePosition(
        this.config.origin,
        tooltip.centroid,
        tooltip.altitude,
      );

      if (this.selectedHouseIds.includes(tooltip.houseId)) {
        tooltip.expanded = true;
      }

      const tooltipNode = createTooltip(tooltip);
      const tooltipObject = new CSS2DObject(tooltipNode);

      tooltipObject.position.copy(tooltipPosition);
      tooltipObject.style = {
        transform: [
          /**
           * Скейлим тултипы в зависимости от зума: меньше зум -> меньше размер тултипа
           * */
          'scale(var(--tooltip-scale))',

          'translateY(-50%)',

          /** При скейлинге тултипов, сдвигаем их по вертикали
           * Формула подобрана имерическим путем
           * */
          'translateY(calc(50% * (1 - var(--tooltip-scale)) * (1 - var(--tooltip-scale))))',
        ].join(' '),
      };

      scene.add(tooltipObject);
    });
  };

  updateTooltipsRendererSize = () => {
    this.tooltipsRenderer.setSize(
      this.map.getCanvas().clientWidth,
      this.map.getCanvas().clientHeight,
    );
  };

  updateTooltipsScale = () => {
    const currentZoom = this.map.getZoom();
    const { minZoom, maxZoom } = TOOLTIPS_SCALE_ZOOM;

    let scale = 1;

    if (currentZoom >= maxZoom) {
      scale = 1;
    } else if (currentZoom <= minZoom) {
      scale = 0;
    } else {
      scale = (currentZoom - minZoom) / (maxZoom - minZoom);
    }

    this.tooltipsRenderer.domElement.style.setProperty(CSS_VARIABLE.tooltipScale, scale);
  };

  setSelectedHouseIds = (selectedHouseIds) => {
    this.selectedHouseIds = selectedHouseIds;

    this.updateTooltipsClasses();
  };

  handleMouseMoveSection = (event) => {
    const [firstFeature] = event.features;

    const houseId = firstFeature.properties.lun_house_id;

    if (this.hoveredHouseId === houseId) {
      // skip updates
      return;
    }

    this.tooltipsRenderer.domElement.style.cursor = 'pointer';

    this.setHoveredHouseId(houseId);
  };

  handleMouseLeaveSection = () => {
    this.setHoveredHouseId(null);

    this.tooltipsRenderer.domElement.style.cursor = '';
  };

  setHoveredHouseId = (hoveredHouseId) => {
    this.hoveredHouseId = hoveredHouseId;

    this.updateTooltipsClasses();
  };

  updateTooltipsClasses = () => {
    const allHouseTooltips = [...document.querySelectorAll('[data-house-id]')];

    allHouseTooltips.forEach((tooltipNode) => {
      const houseId = Number(tooltipNode.dataset.houseId);

      tooltipNode.classList.remove(
        ACTIVE_TOOLTIP_CSS_CLASS.expanded,
        ACTIVE_TOOLTIP_CSS_CLASS.hovered,
      );

      if (houseId === this.hoveredHouseId) {
        tooltipNode.classList.add(ACTIVE_TOOLTIP_CSS_CLASS.hovered);
      } else if (this.selectedHouseIds.includes(houseId)) {
        tooltipNode.classList.add(ACTIVE_TOOLTIP_CSS_CLASS.expanded);
      }
    });
  };
}
